import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Link a portal login to a Telegram account.
 *
 * The portal knows a username ("brad"); Telegram knows a numeric id. Nothing
 * joins them, so the user proves control of both: the dashboard issues a
 * short-lived code, and sending it to the bot completes the link. Until that
 * happens a non-admin has no ticker list to edit.
 */
function LinkTelegram({ getApiUrl, onLinked }) {
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await axios.get(getApiUrl('/link/status'), { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  // Run once on mount. loadStatus is stable enough for this and adding it to
  // the deps would re-fetch on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatus(); }, []);

  const requestCode = async () => {
    setBusy(true); setError(null);
    try {
      const res = await axios.post(getApiUrl('/link/start'), {}, { withCredentials: true });
      setCode(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Admins already see every account; the link only matters for scoping a
  // regular user to their own list.
  if (!status || status.is_admin || status.linked) {
    if (status && status.linked && !status.is_admin) {
      return (
        <div className="dashboard-card third-width">
          <h2>Telegram Link</h2>
          <div className="editor-status ok">
            <strong>{status.portal_username}</strong> is linked to Telegram user{' '}
            <code>{status.linked_telegram_ids[0]}</code>.
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            You can edit your own ticker list below. Ask an admin to change this link.
          </p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="dashboard-card third-width">
      <h2>Link Telegram</h2>
      <div className="editor-status info">
        Your portal account <strong>{status.portal_username}</strong> is not linked to a Telegram
        account yet, so there is no ticker list to show.
      </div>

      {!code && (
        <>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            Generate a code, then send it to the bot from the Telegram account whose alerts you want
            to manage.
          </p>
          <button className="editor-btn primary" onClick={requestCode} disabled={busy}>
            {busy ? 'Generating…' : 'Generate link code'}
          </button>
        </>
      )}

      {code && (
        <>
          <div className="link-code">{code.code}</div>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            Send this to the Telegram bot within {code.expires_in_minutes} minutes:
          </p>
          <div className="link-command">/link {code.code}</div>
          <button className="editor-btn" onClick={loadStatus} style={{ marginTop: '10px' }}>
            I have sent it — check
          </button>
        </>
      )}

      {error && <div className="editor-status error">{error}</div>}
    </div>
  );
}

export default LinkTelegram;
